import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import useGlobalStore from '@/store/useGlobalStore'
import { getCookie, setCookie, deleteCookie } from 'cookies-next/client'
import { useEffect } from 'react'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { getAlpha2WithExceptions } from '@/components/Checkout/_helpers/functions/getAlpha2WithExceptions'
import { hydra } from '@/lib/services/Hydra'
import { getAuthCookie } from '@/lib/utils/shared/cookies/getAuthCookie'
import { logError } from '@/lib/utils/shared/log'

// Track if the hook has ever run in this session
let hasRunInSession = false

export const useOldUserInitialize = () => {
  const {
    app: { alpha2, language },
    user: { isSignedIn },
  } = useGlobalStore()

  const initializeProperties = async () => {
    try {
      const authToken = getAuthCookie()
      if (!authToken) {
        await mixpanelService.reset()
        deleteCookie('userInitializedInMixpanel')
        return
      }

      const customer = await hydra.getMe({
        hydraToken: authToken,
      })

      if (!customer) {
        await mixpanelService.reset()
        deleteCookie('userInitializedInMixpanel')
        return
      }

      const unicityId =
        typeof customer.id.unicity === 'object'
          ? (customer.id.unicity as { unicity: string }).unicity
          : customer.id.unicity

      mixpanelService.identify(unicityId)

      const enrollerId = customer.enroller.id.unicity.toString()
      const sponsorId = customer.sponsor.id.unicity.toString()

      mixpanelService.registerSuperProperties({
        enroller_id: enrollerId,
        sponsor_id: sponsorId || enrollerId,
        user_market: getAlpha2WithExceptions({ alpha2 }),
        user_market_extended: alpha2,
        user_language: language,
        is_user_logged_in: 'true',
        user_type: customer.type,
      })

      await mixpanelService.setPeopleProperties({
        unicity_id: unicityId,
        enroller_id: enrollerId,
        sponsor_id: sponsorId || enrollerId,
        user_market: getAlpha2WithExceptions({ alpha2 }),
        user_market_extended: alpha2,
        user_language: language,
        membership_registration_date: customer.joinDate,
        user_type: customer.type,
      })

      setCookie('userInitializedInMixpanel', true)
    } catch {
      await mixpanelService.reset()
      deleteCookie('userInitializedInMixpanel')
    }
  }

  useEffect(() => {
    const initialize = async () => {
      // Skip if we've already run in this session
      if (hasRunInSession) {
        return
      }

      const authToken = getAuthCookie()
      const isInitialized = getCookie('userInitializedInMixpanel')

      // If we have no auth token but are initialized, clean up
      if (!authToken && isInitialized) {
        await mixpanelService.reset()
        deleteCookie('userInitializedInMixpanel')
        return
      }

      // Only proceed if user is signed in and has auth token
      if (!isSignedIn || !authToken) {
        return
      }

      // If already initialized, mark as done and return
      if (isInitialized) {
        hasRunInSession = true
        return
      }

      try {
        await initializeProperties()
        await mixpanelService.trackEvent(
          MixpanelEvent.INITIALIZE_USER_PROFILE_FOR_OLD_USERS,
          {},
        )
        hasRunInSession = true
      } catch (error) {
        logError('Failed to initialize user:', error)
        await mixpanelService.reset()
        deleteCookie('userInitializedInMixpanel')
      }
    }

    // Use undefined instead of void operator
    initialize().catch((error) => {
      logError('Failed to initialize:', error)
    })
  }, []) // Empty dependency array to run only once on mount
}
